import { CAMPAIGN_STATUS_LABELS, MESSAGE_CHANNEL_LABELS, type RecipientStatus } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton, type BadgeVariant } from '@/components/ui/field'
import { campaignQuery, campaignRecipientsQuery, useCampaignAction } from '@/features/messaging/api'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../_app'

export const Route = createFileRoute('/_app/campaigns/$campaignId')({
  component: CampaignDetailPage,
})

const RECIPIENT_TONE: Record<RecipientStatus, BadgeVariant> = {
  sent: 'success',
  queued: 'muted',
  failed: 'destructive',
  skipped: 'muted',
}

function CampaignDetailPage() {
  const { campaignId } = Route.useParams()
  const { me } = AppRoute.useRouteContext()
  const canSend = me.permissions.includes('campaigns:send')

  const { data, isLoading } = useQuery(campaignQuery(campaignId))
  const recipients = useQuery(campaignRecipientsQuery(campaignId))
  const actions = useCampaignAction(campaignId)

  const campaign = data?.campaign

  if (isLoading || !campaign) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const queued =
    campaign.recipientCount - campaign.sentCount - campaign.failedCount - campaign.skippedCount

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to campaigns
      </Link>

      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold">{campaign.name}</h1>
              <Badge variant={campaign.status === 'done' ? 'success' : 'default'}>
                {CAMPAIGN_STATUS_LABELS[campaign.status]}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              {MESSAGE_CHANNEL_LABELS[campaign.channel]}
              {campaign.scheduledAt && ` · scheduled ${formatDateTime(campaign.scheduledAt)}`}
            </p>
          </div>

          {canSend && (
            <div className="flex gap-2">
              {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                <Button loading={actions.send.isPending} onClick={() => actions.send.mutate()}>
                  Send now
                </Button>
              )}
              {(campaign.status === 'sending' || campaign.status === 'scheduled') && (
                <Button
                  variant="outline"
                  loading={actions.cancel.isPending}
                  onClick={() => actions.cancel.mutate()}
                >
                  Cancel
                </Button>
              )}
              {campaign.failedCount > 0 && (
                <Button
                  variant="outline"
                  loading={actions.retry.isPending}
                  onClick={() => actions.retry.mutate()}
                >
                  Retry {campaign.failedCount} failed
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Recipients" value={campaign.recipientCount} />
          <Stat label="Sent" value={campaign.sentCount} tone="success" />
          <Stat
            label="Failed"
            value={campaign.failedCount}
            tone={campaign.failedCount ? 'destructive' : undefined}
          />
          <Stat label="Skipped" value={campaign.skippedCount} />
        </div>
        {queued > 0 && (
          <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
            {queued.toLocaleString()} still queued — the outbox drains every few minutes.
          </p>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-[var(--color-border)] p-4">
          <h2 className="text-sm font-semibold">Recipients</h2>
        </div>
        {recipients.isLoading && (
          <div className="space-y-2 p-8">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        )}
        {recipients.data && recipients.data.items.length === 0 && !recipients.isLoading && (
          <div className="flex flex-col items-center gap-2 p-8 text-center">
            <Users className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            <p className="text-sm text-[var(--color-muted-foreground)]">No recipients yet.</p>
          </div>
        )}
        {recipients.data && recipients.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-muted)]/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Detail</th>
                  <th className="px-4 py-2 font-medium">Sent</th>
                </tr>
              </thead>
              <tbody>
                {recipients.data.items.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2">
                      {r.clientId ? (
                        <Link
                          to="/clients/$clientId"
                          params={{ clientId: r.clientId }}
                          className="text-[var(--color-primary)] hover:underline"
                        >
                          {r.fullName ?? r.phone ?? r.clientId}
                        </Link>
                      ) : (
                        (r.fullName ?? '—')
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={RECIPIENT_TONE[r.status]}>{r.status}</Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                      {r.reason ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                      {r.sentAt ? formatDateTime(r.sentAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'success' | 'destructive'
}) {
  return (
    <div className="rounded-md border border-[var(--color-border)] p-3">
      <p className="text-xs text-[var(--color-muted-foreground)]">{label}</p>
      <p
        className={cn(
          'text-xl font-semibold',
          tone === 'success'
            ? 'text-[var(--color-success)]'
            : tone === 'destructive'
              ? 'text-[var(--color-destructive)]'
              : '',
        )}
      >
        {value.toLocaleString()}
      </p>
    </div>
  )
}
